/**
 * 客户端环境检测：设备类型、运行环境、浏览器。
 * 基于 UA / navigator 启发式判断，WebView（尤其 iOS WKWebView）无法 100% 准确。
 */

export const DeviceType = {
  ANDROID_PHONE: 'android-phone',
  IPHONE: 'iphone',
  ANDROID_TABLET: 'android-tablet',
  IPAD: 'ipad',
  MAC: 'mac',
  WINDOWS: 'windows',
  UNKNOWN: 'unknown'
}

export const Runtime = {
  /** 微信/FB 等 App 内置浏览器 */
  WEBVIEW: 'webview',
  /** 系统浏览器标签页 */
  BROWSER: 'browser',
  /** 已添加到桌面，standalone 模式打开 */
  PWA: 'pwa'
}

export const Browser = {
  CHROME: 'chrome',
  SAFARI: 'safari',
  FIREFOX: 'firefox',
  EDGE: 'edge',
  OPERA: 'opera',
  SAMSUNG: 'samsung-browser',
  UC: 'uc-browser',
  WECHAT: 'wechat',
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  TIKTOK: 'tiktok',
  UNKNOWN: 'unknown'
}

let cachedEnv = null

function getUA () {
  return typeof navigator !== 'undefined' ? navigator.userAgent : ''
}

function isIPad () {
  const ua = getUA()
  if (/iPad/.test(ua)) return true
  return typeof navigator !== 'undefined' &&
    navigator.platform === 'MacIntel' &&
    navigator.maxTouchPoints > 1
}

function isIPhone () {
  return /iPhone/.test(getUA())
}

function isAndroid () {
  return /Android/i.test(getUA())
}

function isAndroidPhone () {
  return isAndroid() && /Mobile/i.test(getUA())
}

function isAndroidTablet () {
  return isAndroid() && !/Mobile/i.test(getUA())
}

function isMac () {
  if (isIPad()) return false
  return /Macintosh|Mac OS X/i.test(getUA())
}

function isWindows () {
  return /Windows/i.test(getUA())
}

/** 设备类型 */
export function getDeviceType () {
  if (typeof window !== 'undefined' && window.__APP_ENV__?.deviceType) {
    return window.__APP_ENV__.deviceType
  }

  if (isIPad()) return DeviceType.IPAD
  if (isIPhone()) return DeviceType.IPHONE
  if (isAndroidTablet()) return DeviceType.ANDROID_TABLET
  if (isAndroidPhone()) return DeviceType.ANDROID_PHONE
  if (isMac()) return DeviceType.MAC
  if (isWindows()) return DeviceType.WINDOWS
  return DeviceType.UNKNOWN
}

function detectWebViewKind () {
  const ua = getUA()

  if (typeof window !== 'undefined' && window.__APP_ENV__?.isWebView) {
    return window.__APP_ENV__.webviewKind || 'native-webview'
  }

  if (/MicroMessenger/i.test(ua)) return 'wechat'
  if (/FBAN|FBAV/i.test(ua)) return 'facebook'
  if (/Instagram/i.test(ua)) return 'instagram'
  if (/BytedanceWebview|TikTok/i.test(ua)) return 'tiktok'
  if (/Line\//i.test(ua)) return 'line'
  if (/; wv\)/.test(ua)) return 'android-webview'

  if (typeof window !== 'undefined') {
    if (window.Android || window.android) return 'android-webview'
    if (window.webkit?.messageHandlers &&
      Object.keys(window.webkit.messageHandlers).length > 0) {
      return 'ios-webview'
    }
  }

  return null
}

function isPWA () {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  )
}

/** 运行环境：WebView / 浏览器 / PWA */
export function getRuntime () {
  if (typeof window !== 'undefined' && window.__APP_ENV__?.runtime) {
    return window.__APP_ENV__.runtime
  }

  if (isPWA()) return Runtime.PWA

  const webViewKind = detectWebViewKind()
  if (webViewKind) return Runtime.WEBVIEW

  return Runtime.BROWSER
}

/** 浏览器 / 内置 WebView 容器名称 */
export function getBrowser () {
  if (typeof window !== 'undefined' && window.__APP_ENV__?.browser) {
    return window.__APP_ENV__.browser
  }

  const ua = getUA()
  const webViewKind = detectWebViewKind()

  if (webViewKind === 'wechat') return Browser.WECHAT
  if (webViewKind === 'facebook') return Browser.FACEBOOK
  if (webViewKind === 'instagram') return Browser.INSTAGRAM
  if (webViewKind === 'tiktok') return Browser.TIKTOK

  if (/SamsungBrowser/i.test(ua)) return Browser.SAMSUNG
  if (/UCBrowser/i.test(ua)) return Browser.UC
  if (/Edg/i.test(ua)) return Browser.EDGE
  if (/OPR|Opera/i.test(ua)) return Browser.OPERA
  if (/Firefox/i.test(ua)) return Browser.FIREFOX
  if (/CriOS/i.test(ua)) return Browser.CHROME
  if (/Chrome/i.test(ua) && !/Edg|OPR|SamsungBrowser/i.test(ua)) return Browser.CHROME
  if (/Safari/i.test(ua) && !/Chrome|CriOS|Edg|OPR|SamsungBrowser/i.test(ua)) return Browser.SAFARI

  if (webViewKind === 'android-webview' || webViewKind === 'ios-webview') {
    return Browser.UNKNOWN
  }

  return Browser.UNKNOWN
}

/**
 * 完整环境信息
 * @param {{ refresh?: boolean }} options refresh=true 时强制重新检测
 */
export function getClientEnv (options = {}) {
  if (cachedEnv && !options.refresh) return cachedEnv

  const deviceType = getDeviceType()
  const runtime = getRuntime()
  const browser = getBrowser()
  const webViewKind = detectWebViewKind()

  cachedEnv = {
    deviceType,
    runtime,
    browser,
    webViewKind,
    isPWA: runtime === Runtime.PWA,
    isWebView: runtime === Runtime.WEBVIEW,
    isMobile: [
      DeviceType.ANDROID_PHONE,
      DeviceType.IPHONE,
      DeviceType.ANDROID_TABLET,
      DeviceType.IPAD
    ].includes(deviceType),
    isTablet: deviceType === DeviceType.IPAD || deviceType === DeviceType.ANDROID_TABLET
  }

  return cachedEnv
}

/** 重置缓存（一般不需要，测试或 UA 变化时可用） */
export function resetClientEnvCache () {
  cachedEnv = null
}
